import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Checkbox,
  Hint,
  Inscription,
  NewTabIcon,
  Text,
  useResponsive,
} from '@common-fe/common-fe';

import { CheckboxGuard, ClickableRow, ShakeBox } from '../ReviewAddressModal.styles';

interface Props {
  termsAccepted: boolean;
  isAgreementOpened: boolean;
  isWarningShaking: boolean;
  onOpenAgreement: () => void;
  onToggleTerms: () => void;
  onTriggerShake: () => void;
}

const TermsAgreementSection: React.FC<Props> = ({
  termsAccepted,
  isAgreementOpened,
  isWarningShaking,
  onOpenAgreement,
  onToggleTerms,
  onTriggerShake,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();

  return (
    <Box
      pad={isMobile ? 'spacing16' : 'spacing24'}
      background="canvas"
      round="container1Round"
      border={{ color: 'border2', size: '1px' }}
      gap="spacing16"
      data-testid="bad-address-review_terms-section"
    >
      <Text size="large" color="textBody">
        <Text color="danger" weight="bold">
          {'* '}
        </Text>
        {t('Terms and Agreements')}
      </Text>
      <ClickableRow
        direction="row"
        align="center"
        data-testid="bad-address-review_terms-row"
        onClick={onOpenAgreement}
      >
        <CheckboxGuard
          $locked={!isAgreementOpened}
          data-testid="bad-address-review_terms-checkbox-wrapper"
          onClick={(e) => {
            if (!isAgreementOpened) {
              e.stopPropagation();
              onTriggerShake();
            }
          }}
        >
          <Hint
            hintAlign="left"
            hintWidth={220}
            hideHint={isAgreementOpened}
            testId="bad-address-review_terms-hint"
            hintElement={(
              <Checkbox
                testId="bad-address-review_terms"
                name="termsAccepted"
                checked={termsAccepted}
                disabled={!isAgreementOpened}
                onClick={(e) => e.stopPropagation()}
                onChange={onToggleTerms}
              />
            )}
          >
            <Inscription size="12px" weight={500} textAlign="center">
              {t('Open the HSA Custodial Agreement first to enable this checkbox.')}
            </Inscription>
          </Hint>
        </CheckboxGuard>
        <Box direction="row" align="center" gap="spacing4">
          <Text color="textBody">{t('I have reviewed the')}</Text>
          <Box direction="row" align="center" gap="spacing4" data-testid="bad-address-review_terms-link">
            <Text color="textAccent" weight="bold">
              {t('HSA Custodial Agreement')}
            </Text>
            <NewTabIcon size="24px" color="iconAccent" />
          </Box>
        </Box>
      </ClickableRow>

      {!termsAccepted && (
        <ShakeBox
          $shake={isWarningShaking}
          pad="spacing24"
          background="warningContainer"
          border={{ color: 'warningBorder', size: '1px' }}
          round="fieldRound"
          data-testid="bad-address-review_terms-warning"
          style={{ position: 'relative' }}
        >
          <Box
            height="2px"
            round="small"
            background="warning"
            style={{ position: 'absolute', top: '-0.5px', left: '24px', right: '24px' }}
          />
          <Text color="textBody">
            {t('Open all agreements prior to agreeing to reviewing each item.')}
          </Text>
        </ShakeBox>
      )}
    </Box>
  );
};

export default TermsAgreementSection;
